// Create room dialog: name, map, player limit, public or private (invite code).

import { useEffect, useRef, useState } from 'preact/hooks';
import { stripColors } from '../etColors';
import { useApp } from '../context';
import { createRoomDialog, net, settings } from '../state';
import { Button } from './Button';
import { TextInput } from './Field';
import { Levelshot } from './Levelshot';
import { Select } from './Select';
import { Slider } from './Slider';
import { Toggle } from './Toggle';

export function CreateRoomDialog() {
  const dialog = createRoomDialog.value;
  if (!dialog) return null;
  return <Dialog initialMap={dialog.map} />;
}

function Dialog({ initialMap }: { initialMap: string | null }) {
  const app = useApp();
  const n = net.value;
  const maps = [...n.maps].sort((a, b) => stripColors(a.longname ?? a.name).localeCompare(stripColors(b.longname ?? b.name)));
  const [name, setName] = useState(`${settings.peek().playerName}'s room`);
  const [map, setMap] = useState(initialMap && maps.some((m) => m.name === initialMap) ? initialMap : (maps[0]?.name ?? ''));
  const [maxPlayers, setMaxPlayers] = useState(16);
  const [isPrivate, setPrivate] = useState(false);
  const [practice, setPractice] = useState(false);
  const nameRef = useRef<HTMLDivElement>(null);
  const close = () => (createRoomDialog.value = null);

  useEffect(() => {
    nameRef.current?.querySelector('input')?.focus();
  }, []);

  const create = () => {
    if (!map) return;
    close();
    app.createRoom({ name: name.trim(), map, maxPlayers, private: isPrivate, practice });
  };

  return (
    <div class="modal-backdrop layer" data-layer="modal" role="dialog" aria-modal="true" aria-labelledby="create-room-title" onClick={(e) => e.target === e.currentTarget && close()}>
      <form
        class="modal create-room"
        onSubmit={(e) => {
          e.preventDefault();
          create();
        }}
      >
        <h2 id="create-room-title" class="modal-title">
          Create room
        </h2>
        <div class="create-room-body">
          <Levelshot map={map} eager class="create-room-shot" />
          <div class="create-room-form">
            <div class="stack-field" ref={nameRef}>
              <span class="field-label">Room name</span>
              <TextInput value={name} onChange={setName} maxLength={32} label="Room name" />
            </div>
            <Select label="Map" value={map} options={maps.map((m) => ({ value: m.name, label: `${stripColors(m.longname ?? m.name)} (${m.name})` }))} onChange={setMap} />
            <Slider label="Max players" value={maxPlayers} min={1} max={32} step={1} onChange={setMaxPlayers} />
            <Toggle label="Private" hint="Not listed in the lobby; players join with the invite code." checked={isPrivate} onChange={setPrivate} />
            <Toggle label="Practice" hint="Players may /goto and /call each other (not during a timed run)." checked={practice} onChange={setPractice} />
          </div>
        </div>
        <div class="modal-actions">
          <Button variant="ghost" onClick={close} hint="ESC">
            Cancel
          </Button>
          <Button variant="primary" type="submit" icon="play" disabled={!map}>
            Create
          </Button>
        </div>
      </form>
    </div>
  );
}
